<script setup lang="ts">
    // @ts-nocheck
    import KButton from "@/components/Button.vue";
    import { CALENDAR_VIEWS } from "@/components/calendars/helpers";
    import { CalendarApi } from "@fullcalendar/core";
    import { CalendarData } from "@fullcalendar/core/internal";
    import moment, { unitOfTime } from "moment";
    import { nextTick, onBeforeMount, onMounted, ref, toRefs } from "vue";
    import { flashMessage } from "@/utils";
    import VardplatsSparraTid from "@/tabs/Vardplatsbokning/VardplatsSparraTid.vue";

    // Hårdkodad typning som inte är exporterad, pga kan inte ha externa typer innan Vue3
    interface CalendarToolbarProps {
        // Ska navigationen (framåt/bakåt) visas?
        showNavigation?: boolean;
        // Ska idag-knappen visas?
        showTodayButton?: boolean;
        // Ska imorgon-knappen visas?
        showTomorrowButton?: boolean;
        // Ska datumväljaren visas?
        showDatePicker?: boolean;
        // Funktion för att öppna filtret. Används vid klick av filter-knappen
        onFilterClick?: () => {
            isActive: boolean;
        };
        calendarApi?: CalendarApi & { currentData: CalendarData };
        /*
         Mängden dagar/veckor/år som framåt/bakåt-knapparna i navigationen ska hoppa,
         istället för Fullcalendars inbyggda mängd
         */
        navNextOverride?: {
            amount: number;
            unit: unitOfTime.DurationConstructor;
        };
        /*
         Mängden dagar/veckor/år som framåt/bakåt-knapparna i navigationen ska hoppa,
         istället för Fullcalendars inbyggda mängd
         */
        navPrevOverride?: {
            amount: number;
            unit: unitOfTime.DurationConstructor;
        };
        // View type från Fullcalendar
        viewType: string;
        // Används helger i kalendern? Påverkar datumväljaren
        weekends?: boolean;
        // Dolda dagar i kalendern. ANvänds av datumväljaren
        hiddenDays?: number[];
        // Ska titeln bara skriva ut det exakt valda datumet i kalendern och inte en hel range (t.ex vid veckovy)
        singleDateTitle?: boolean;
    }

    const props = withDefaults(defineProps<CalendarToolbarProps>(), {
        showNavigation: true,
        showTodayButton: true,
        showTomorrowButton: true,
        showDatePicker: true,
        singleDateTitle: false,
        hiddenDays: () => [],
    });

    const emit = defineEmits<{
        (event: "dateChange", payload: CalendarData): void;
        (event: "viewChange", payload: CalendarData): void;
    }>();

    const { calendarApi, navNextOverride, navPrevOverride, weekends, hiddenDays } = toRefs(props);

    const isFilterActive = ref(false);
    const dateTitle = ref("");
    const currentDate = ref(new Date());

    const showSparraTid = ref(false);

    const calendarDatepickerRef = ref();

    const toggleSparraTid = () => {
        showSparraTid.value = !showSparraTid.value;
    };

    const onCreatedSparradTid = () => {
        toggleSparraTid();

        flashMessage("Tid spärrad");
    };

    /**
     * Hoppa till föregående datum i kalendern
     */
    const previous = () => {
        if (navPrevOverride?.value) {
            const subtractedDate = moment(new Date(calendarApi?.value?.currentData?.currentDate ?? ""))
                .subtract(navPrevOverride.value.amount, navPrevOverride.value.unit)
                // Behövs för att tiden, pga tz, inte hoppar 1 timme framåt vid varje previous()
                .set({ hours: 7, minutes: 0, seconds: 0 });

            if (!weekends?.value && [6, 7].includes(moment(subtractedDate).isoWeekday())) {
                calendarApi?.value?.gotoDate(moment(subtractedDate).subtract("week", 1).day(5).toDate());
            } else {
                calendarApi?.value?.gotoDate(moment(subtractedDate).toDate());
            }
        } else {
            calendarApi?.value?.prev();
        }
        setDateAndTitle();
    };

    /**
     * Hoppa till nästa datum i kalendern
     */
    const next = () => {
        if (!calendarApi?.value) {
            return;
        }
        if (navNextOverride?.value) {
            const value = moment(calendarApi.value?.currentData?.currentDate)
                .set({ hours: 7, minutes: 0, seconds: 0 })
                .add(navNextOverride.value.amount, navNextOverride.value.unit)
                .toDate();
            calendarApi.value?.gotoDate(value);
        } else {
            calendarApi.value?.next();
        }

        setDateAndTitle();
    };

    /**
     * Uppdatera KDatepickers datum
     */
    const setKDateTimePickerDate = (newDate: Date) => {
        calendarDatepickerRef?.value?.setValue?.(newDate);
    };
    /**
     * Ändra vy i kalendern
     */
    const changeView = ({ viewType }: { viewType: keyof typeof CALENDAR_VIEWS }) => {
        if (!calendarApi?.value) {
            return;
        }

        calendarApi.value.changeView(viewType);

        emit("viewChange", calendarApi.value.currentData);
    };

    /**
     * Hoppa till dagens datum i kalendern
     */
    const today = () => {
        calendarApi?.value?.today();
        setDateAndTitle();
    };

    /**
     * Hoppa till morgondagens datum i kalendern
     */
    const tomorrow = () => {
        calendarApi?.value?.gotoDate(moment().add(1, "days").toDate());
        setDateAndTitle();
    };

    /**
     * Kan kallas när datum har satts, när både titel och picker behöver uppdateras
     */
    const setDateAndTitle = () => {
        setKDateTimePickerDate(calendarApi.value.getDate());
        setTitle();
    };

    /**
     * Hoppa till specifikt datum i kalendern
     */
    const changeDate = (dateString?: string) => {
        if (!dateString) {
            return;
        }
        calendarApi?.value?.gotoDate(dateString);

        setTitle();
    };

    /**
     * Sätter ny titel baserat på valt datum i kalendern.
     * Emittar även dateChange uppåt
     * Sättning av ny title (datum). Måste sättas manuellt, pga att ändringar i currentData inte triggrar computed
     */
    const setTitle = () => {
        if (!calendarApi?.value?.currentData) {
            dateTitle.value = "";

            return;
        }

        const viewTitle = calendarApi.value.currentData.viewTitle;

        if (typeof viewTitle === "string" && dateTitle.value !== viewTitle) {
            emit("dateChange", calendarApi.value.currentData);
        }

        currentDate.value = calendarApi.value.currentData.currentDate;
        dateTitle.value = new Date(calendarApi.value.currentData.currentDate).toLocaleDateString("sv", {
            weekday: "long",
            month: "short",
            year: "numeric",
            day: "numeric",
        });
    };

    onBeforeMount(() => {
        setTitle();
    });

    onMounted(async () => {
        // Måste vänta för att calendarApi ska vara satt inann första sättningen av titel
        await nextTick();
        setTitle();
    });
</script>

<template>
    <div class="calendar-toolbar">
        <div class="date">
            <span>{{ dateTitle }}</span>
        </div>
        <div class="actions">
            <div v-if="onFilterClick" class="filter">
                <KButton
                    type="button"
                    variant="hollow"
                    size="adaptable"
                    :class="['filter-button', { active: isFilterActive }]"
                    @click="onFilterClick"
                >
                    <img src="@/assets/filter.svg" alt="filtrering" />
                    <span>Filtrering</span>
                </KButton>
            </div>

            <div class="sparra-tid-tab">
                <KButton type="button" variant="hollow" size="adaptable" @click="toggleSparraTid">
                    <img src="@/assets/block_time.svg" alt="spärra" />
                    <span>Spärra tid</span>
                </KButton>
            </div>
            <div v-if="$slots.customActions" class="custom">
                <slot
                    name="customActions"
                    :changeView="changeView"
                    :changeDate="changeDate"
                    :tomorrow="tomorrow"
                    :today="today"
                    :next="next"
                    :previous="previous"
                    :viewType="viewType"
                    :calendarApi="calendarApi"
                />
            </div>
            <div v-if="$slots.viewButtons" class="date-buttons">
                <slot name="viewButtons" :changeView="changeView" :viewType="viewType" />
            </div>
            <div v-if="$slots.dateButtons || showTodayButton || showTomorrowButton" class="date-buttons">
                <KButton
                    v-if="showTodayButton"
                    type="button"
                    variant="hollow"
                    size="adaptable"
                    @click="today"
                    :isActive="moment(currentDate).isSame(Date.now(), 'day')"
                >
                    Idag
                </KButton>
                <KButton
                    v-if="showTomorrowButton"
                    type="button"
                    variant="hollow"
                    size="adaptable"
                    @click="tomorrow"
                    :isActive="moment(currentDate).isSame(moment().add(1, 'day'), 'day')"
                >
                    Imorgon
                </KButton>
                <slot name="dateButtons" />
            </div>
            <div v-if="$slots.datePicker || showDatePicker" class="search">
                <slot name="datepicker">
                    <KDateTimepicker
                        ref="calendarDatepickerRef"
                        class="datepicker"
                        placeholder="Sök datum"
                        name="calendar-datepicker"
                        :disabled-dates="hiddenDays"
                        :disabled-days="!weekends ? [0, 6] : undefined"
                        @inputValue="changeDate"
                        convertTo="date"
                        layout="compact"
                    />
                </slot>
            </div>
            <div v-if="$slots.navigation || showNavigation" class="navigation">
                <slot name="navigation">
                    <KButton type="button" size="square-icon" @click="previous">
                        <img src="@/assets/arrow_left_white.svg" alt="föregående" />
                    </KButton>
                    <KButton type="button" size="square-icon" @click="next">
                        <img src="@/assets/arrow_right_white.svg" alt="nästa" />
                    </KButton>
                </slot>
            </div>
        </div>
        <VardplatsSparraTid v-if="showSparraTid" @created="onCreatedSparradTid" @cancel="toggleSparraTid" />
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/variables";

    .calendar-toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 3.5rem;
        box-sizing: border-box;
        padding: 0.8rem 0.6rem;
        background-color: $color-blue-light;
        border-radius: $border-radius;

        > * {
            height: 100%;
        }

        .date {
            color: $color-default-blue;
            font-size: 1.2rem;
            text-transform: capitalize;
        }

        ::v-deep .button {
            min-height: unset;
            max-height: 100%;
            color: $color-default-blue;
        }

        .actions {
            > * {
                height: 100%;
                display: flex;
                align-items: center;
                font-size: 0.9rem;
            }

            .navigation {
                display: flex;
                gap: 1rem;
            }
        }

        .actions,
        .date {
            display: flex;
            align-items: center;
            gap: 0.8rem;
        }

        .search {
            .datepicker {
                margin: 0;
                width: 8rem;
                height: 100%;
            }
        }
    }
</style>
